import { LitElement, html, css, TemplateResult } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { HighlightPastTrailActions, PastTrailData, VoyagePastTrailModel, VoyagePastTrailModelEvents } from '../../../src/models/voyage-past-trail-model';
import { GetFormattedTimeWithDate } from '../../../src/utils/helpers';
import { getFormattedDataSource } from '../../../src/utils/utils';

export const tableTitles = ["Date", "Draft", "Latitude", "Longitude", "Speed", "Course", "Heading", "Source", "Navigational status", "Wind 50 m", "Wind gusts 50 m", "Wave", "Swell", "Risk wave height", "Currents"];
export const positionCols: Array<keyof PastTrailData> = ["timestamp", "draft", "latitude", "longitude", "speed", "course", "heading", "source", "navigationalStatus"];
export const weatherCols: Array<keyof PastTrailData> = ["wind50M", "windGusts50M", "wave", "swell", "riskWaveHeight", "currents"];

export const formatPastTrailValue = (value: unknown, property: string) => {
    if (!value) {
        return '';
    }

    switch (property) {
        case "timestamp":
            return `${GetFormattedTimeWithDate(<number>value)} UTC`;
        case "speed":
            return `${(<number>value)?.toFixed(1)} kt`;
        case "source":
            return getFormattedDataSource(<string>value);
        default:
            return value;
    }
}

@customElement('voy-past-trail-table')
export class VoyPastTrailTable extends LitElement {
    @property({ attribute: false }) public showWeather: boolean = false;
    @property({ attribute: false }) public exportExcel: boolean = false;
    @property({ attribute: false }) public tableExpanded: boolean = false;
    @property({ attribute: false }) public positions: PastTrailData[] | null = null;
    @property({ attribute: false }) public fromDate?: string
    @property({ attribute: false }) public toDate?: string
    @property({ attribute: false }) public maximized: boolean = false;
    @property({ attribute: false }) public _hovering: number = -1
    @property({ attribute: false }) public _currentIdx: number = -1
    @property({ attribute: false }) public _currentwp = {}
    @property({ attribute: false }) public _isRowClicked: boolean = false
    @property({ attribute: false }) public _currentClickedIndex: number = -1

    constructor() {
        super();
        this._scrollToHighlightedIndex = this._scrollToHighlightedIndex.bind(this);
        this._KeyBoardActions = this._KeyBoardActions.bind(this);
        document.addEventListener("keydown", this._KeyBoardActions);
    }
    static styles = css`
        .default-grid-columns {
            display: grid;
            grid-template-rows: auto;
            font-size: 10px;
            max-height: 360px;
            overflow: auto;
            grid-template-columns: repeat(15, minmax(60px, 100px));
            position: relative;
        }
        .default-grid-columns[maximized]{
            grid-template-columns: repeat(15, minmax(60px, auto));
            max-height: 360px;
            overflow-y: scroll;
        }
        .default-grid-columns > div {
            padding: 8px;
            border-right: 1px solid var(--neutrals-grey-06);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .cut-view {
            overflow-x: scroll ;
        }
        .with-background-color{
            background-color: var(--neutrals-grey-09);
        }
        .titles {
            border-top: 1px solid var(--neutrals-grey-06);
            border-right: 1px solid var(--neutrals-grey-06);
            font-weight: bold;
            position: sticky;
            background-color: white;
            top: 0;
        }
        .cell[background] {
            background-color: var(--neutrals-grey-09);
        }
        .row-div[blue-background] {
            background-color: #B5E0F580;
        }
        .row-div{
            cursor: pointer;
        }

    `;
    connectedCallback() {
        super.connectedCallback();
        VoyagePastTrailModel?.addEventListener(VoyagePastTrailModelEvents.PastTrailClicked, <EventListener>((e: CustomEvent) => {
            this._hightLightTableRow(e.detail)
        }));
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        VoyagePastTrailModel?.removeEventListener(VoyagePastTrailModelEvents.PastTrailClicked, <EventListener>this._hightLightTableRow);
    }

    render() {
        return html`
            <section class="cut-view">
                <div class="default-grid-columns" ?maximized=${this.maximized}>
                    ${tableTitles.map(title => html`<div class="titles">${title}</div>`)}
                        ${this.positions?.map((wp, idx) => this._renderRow(wp, idx))}
                    </div>
            </section>
        `;
    }

    private _renderRow(wp: PastTrailData, idx: number): TemplateResult {
        const hasBackground = idx % 2 === 0;
        const emptyWeatherCols = ["--", "--", "--", "--", "--", "--"];
        return html`
            ${positionCols.map((col) => html`<div class="row-div"  @mouseleave =${() => { this._hovering = -1 }} @mouseenter=${() => {
            this._highlightPastTrail(idx, wp, HighlightPastTrailActions.Focus, "focus")
            this._isRowClicked = false;
        }} @click=${() => {
            this._highlightPastTrail(idx, wp, HighlightPastTrailActions.Click, "click")
        }} ?blue-background=${this._hovering === idx || this._currentClickedIndex === idx}>${this._renderCell(wp[<keyof PastTrailData>col], hasBackground, col)}</div>`)}

            ${this.showWeather
                ? weatherCols.map((col) => html`<div class="row-div" ?blue-background=${this._hovering === idx}>${this._renderCell(wp[<keyof PastTrailData>col], hasBackground, col)}</div>`)
                : emptyWeatherCols.map((col) => html` <div class="row-div" ?blue-background=${this._hovering === idx}>${this._renderCell(col, hasBackground)}</div>`)
            }
        `
    }

    private _renderCell(value: unknown, hasBackground: boolean, property?: string): TemplateResult {
        const renderValue = property && formatPastTrailValue(value, property) || value;
        return html`
             <div class="cell">
                ${(renderValue || renderValue === '') ? renderValue : "--"}
            </div>
        `
    }

    private _highlightPastTrail(idx: number, wp: PastTrailData, action: HighlightPastTrailActions, click: String) {
        if (action === 'click') {
            // Saving the clicked row index
            this._currentClickedIndex = idx
            this._isRowClicked = true
        }
        this._hovering = idx;
        this._currentIdx = idx;
        this._currentwp = wp;
        VoyagePastTrailModel?.highlightPastTrailPoint(wp, action);
    }

    private _hightLightTableRow(detail:any){
        if(detail){
            this.positions?.map((val,idx) => {
                if(val.positionData){
                    if(val.positionData?.latitude === detail.positionData?.latitude && val.positionData?.longitude === detail.positionData?.longitude) {
                        this._hovering = idx;
                    // When row is clicked its preventing the clicked row to reach top
                    if (!this._isRowClicked) {
                        this. _scrollToHighlightedIndex();
                    }
                    }
                }          
            }) 
        } 
    }

    private _scrollToHighlightedIndex() {
        const shadowRoot = this.shadowRoot;
        let tableContainer
        if (shadowRoot) {
            tableContainer = shadowRoot.querySelector('.default-grid-columns');
            if (tableContainer) {
                const rowIndexToScrollTo = this._hovering;

                // Calculate the scroll position
                const rowHeight = 40;
                const scrollToPosition = (rowIndexToScrollTo) * rowHeight;
                // Scroll to the calculated position
                tableContainer.scrollTop = scrollToPosition;
            }
        }
    }

    private _KeyBoardActions(event: { key: String }) {
        if (event.key === "Enter") {
            this._highlightPastTrail(this._currentIdx, this._currentwp as PastTrailData, HighlightPastTrailActions.Click, "")
        }
    }
}
